import React from 'react';
import { ArrowRightIcon, BedDoubleIcon, PhoneIcon, UserRoundIcon, PackageIcon, HomeIcon, MapPinIcon } from 'lucide-react';
import { useFunnel } from '../../contexts/FunnelContext';
import { phones, isLive, siteConfig } from '../../data/siteConfig';
import type { StringKey } from '../../data/i18n';
import type { Bedrooms } from '../../types/funnel';
import { usd } from '../../utils/format';
import type { PropertyKey } from '../../data/propertyImages';
import { BrandPhoto } from './BrandPhoto';
import { PropertyPhoto } from './PropertyPhoto';
import { HelpAnswers } from './HelpAnswers';
import { PhotoGallery } from './PhotoGallery';
import { TrustSection } from './TrustSection';
const sizeCards: {
  bedrooms: Bedrooms;
  title: StringKey;
  hint: StringKey;
  beds: number;
  property: PropertyKey;
}[] = [{
  bedrooms: '1',
  title: 'card1',
  hint: 'crewHint2',
  beds: 1,
  property: 'apt_small'
}, {
  bedrooms: '2',
  title: 'card2',
  hint: 'crewHint23',
  beds: 2,
  property: 'apt_mid'
}, {
  bedrooms: '3',
  title: 'card3',
  hint: 'crewHint3',
  beds: 3,
  property: 'house_small'
}, {
  bedrooms: '4',
  title: 'card4',
  hint: 'crewHint3r',
  beds: 4,
  property: 'house_mid'
}];
export function HomeLanding() {
  const {
    t,
    lang,
    start,
    config
  } = useFunnel();
  // Reserve/sign/pay wording only once paid booking is live and tested; until then the copy describes what actually happens.
  const bookingLive = isLive() && siteConfig.depositCheckoutEnabled;
  const cta = t('ctaBuild');
  return <>
      <section className="bg-paper" data-id="element-466" data-mp="1vbh6xojtbbmf">
        <div className="mx-auto max-w-6xl px-4 pb-12 pt-8 sm:px-6 lg:pt-12" data-id="element-467" data-mp="1vafi8h9viqm8">
          <div className="grid gap-8 lg:grid-cols-[1.25fr_1fr] lg:items-center" data-id="element-468" data-mp="1qc5psefja44b">
            <div data-id="element-469" data-mp="1mbu2suzp7rpo">
              <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-ink sm:text-5xl" data-id="element-470" data-mp="1mcdngjgqgu7e">
                <span className="text-magenta" data-id="element-471" data-mp="3kpnaltok0mvn">{t('homeTitle1')}</span>
                <br data-id="element-472" data-mp="3gp3gkyza1zdq" />
                {t('homeTitle2')}
              </h1>
              <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink-600" data-id="element-473" data-mp="1e2odwnfrrf1g">{bookingLive ? t('homeSub') : t('homeSubPreview')}</p>
              <p className="mt-3 max-w-xl text-[15px] font-semibold text-ink" data-id="element-474" data-mp="1iug5n1anvqt6">{bookingLive ? t('homeSteps') : t('homeStepsPreview')}</p>
              <p className="mt-3 flex items-start gap-2 text-sm font-medium text-ink-700" data-id="element-475" data-mp="153uhoy6coqof">
                <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lime-dark" aria-hidden="true" data-id="element-476" data-mp="193uygpgx9dyn" />
                <span data-id="element-477" data-mp="1wtz0isegp5nj">
                  {t('homeArea')} <span className="font-normal text-ink-500" data-id="element-478" data-mp="21s0krgnmjb0l">{t('homeAreaDetail')}</span>
                </span>
              </p>
            </div>
            <figure className="relative hidden lg:block" data-id="element-479" data-mp="1nrpuaeq87yfy">
              <PropertyPhoto property="townhome" lang={lang} priority className="aspect-[16/10] w-full rounded-2xl" data-id="element-480" data-mp="1src8aqdvpipt" />
              <figcaption className="absolute bottom-3 left-3 rounded-lg bg-ink/85 px-3 py-1.5 text-xs font-semibold text-white" data-id="element-481" data-mp="cd2pebseom6d">
                {lang === 'es' ? 'Apartamentos, casas y townhouses en Florida' : 'Apartments, houses and townhouses across Florida'}
              </figcaption>
            </figure>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4" data-id="element-482" data-mp="4t9yh2y5w13t">
            {sizeCards.map(c => <button key={c.bedrooms} type="button" onClick={() => start(c.bedrooms)} className="group flex flex-col overflow-hidden rounded-2xl border-2 border-ink-200 bg-white text-left transition-[border-color,transform] duration-150 ease-out hover:border-ink active:scale-[0.98]" data-id="element-483" data-mp="iri21tppe0ny">
                <span className="relative block" aria-hidden="true" data-id="element-484" data-mp="3w0wapjyy5bwa">
                  <PropertyPhoto property={c.property} lang={lang} priority decorative className="aspect-[4/3] w-full" data-id="element-485" data-mp="3r8w4j9sa9mr9" />
                  <span className="absolute bottom-2 left-2 flex items-center gap-0.5 rounded-md bg-white/95 px-1.5 py-1 text-lime-dark" data-id="element-486" data-mp="33is1b3j0bp0s">
                    {Array.from({
                  length: c.beds
                }).map((_, i) => <BedDoubleIcon key={i} className="h-4 w-4" strokeWidth={2} data-id="element-487" data-mp="21umy34twu0x2" data-mpi={i} />)}
                  </span>
                </span>
                <span className="flex flex-1 flex-col p-4" data-id="element-488" data-mp="2au1qi9tn0lk7">
                  <span className="font-display text-lg font-extrabold leading-tight text-ink sm:text-xl" data-id="element-489" data-mp="2asgro80aiggj">{t(c.title)}</span>
                  <span className="mt-1 text-sm text-ink-500" data-id="element-490" data-mp="2fml4h7w8lipj">{t(c.hint)}</span>
                  <span className="mt-auto flex items-center gap-1 pt-3 text-sm font-semibold text-magenta-dark" data-id="element-491" data-mp="2kjlbbl0wuphp">
                    {cta} <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-492" data-mp="2pkr057rg9sly" />
                  </span>
                </span>
              </button>)}
          </div>

          <div className="mt-4 flex flex-wrap gap-3" data-id="element-493" data-mp="1051e96hhrw8b">
            <button type="button" onClick={() => start('5')} className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 text-sm font-semibold text-ink hover:border-ink" data-id="element-494" data-mp="145lbea22ezug">
              <HomeIcon className="h-4 w-4 text-ink-500" aria-hidden="true" data-id="element-495" data-mp="rvluawxnum7j" />
              {t('more5')}
            </button>
            <button type="button" onClick={() => start('few')} className="inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-ink-200 bg-white px-4 text-sm font-semibold text-ink hover:border-ink" data-id="element-496" data-mp="mxam0qn0kjrf">
              <PackageIcon className="h-4 w-4 text-ink-500" aria-hidden="true" data-id="element-497" data-mp="1fkel6ezfe3va" />
              {t('fewItems')}
            </button>
          </div>
          <p className="mt-3 text-xs text-ink-500" data-id="element-498" data-mp="16jzxj8yzocom">{lang === 'es' ? 'Las fotos de viviendas son ejemplos de referencia de tamaño.' : 'Home photos are examples for size reference.'}</p>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-499" data-mp="11nhtycfeakdv">
            <button type="button" onClick={() => start()} className="flex h-14 items-center justify-center gap-2 rounded-xl bg-magenta px-7 text-[19px] font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-magenta-dark active:scale-[0.98]" data-id="element-500" data-mp="3rbs8mnrfunuh">
              {cta}
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-501" data-mp="3eqto7evpmbhc" />
            </button>
            <div data-id="element-502" data-mp="3isp17gn06day">
              <a href={phones.ai.href} className="inline-flex min-h-[44px] items-center gap-2 text-base font-bold text-ink underline decoration-lime decoration-2 underline-offset-4" data-id="element-503" data-mp="3is5gjs5yxat8">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-504" data-mp="39t1viin8qlc3" />
                {t('callKyle')}: {phones.ai.display}
              </a>
              <p className="text-xs text-ink-500" data-id="element-505" data-mp="2v5p8ephsc7jl">
                {t('aiLabel')} ·{' '}
                <a href={phones.human.href} className="font-semibold text-ink-700 underline underline-offset-2" data-id="element-506" data-mp="303ib38jvqjf4">
                  {t('humanHelp')}: {phones.human.display}
                </a>
              </p>
            </div>
          </div>
          <p className="mt-6 max-w-2xl border-l-4 border-lime pl-4 text-base font-medium leading-relaxed text-ink" data-id="element-507" data-mp="27gh6u5rknls0">{bookingLive ? t('promise') : t('promisePreview')}</p>
        </div>
      </section>

      <section className="border-t border-ink-200 bg-white" data-id="element-508" data-mp="2gh6zx1pg6y21">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1.4fr_1fr]" data-id="element-509" data-mp="2gii6tae6wlxd">
          <div data-id="element-510" data-mp="2ceadt10p8jtc">
            <h2 className="font-display text-2xl font-black text-ink" data-id="element-511" data-mp="2chy9o6tm9ib8">{t('howTitle')}</h2>
            <ol className="mt-6 grid gap-6 sm:grid-cols-3" data-id="element-512" data-mp="2qda4ee7rv8s5">
              {[{
              k: 'how1' as StringKey,
              b: 'how1Body' as StringKey
            }, {
              k: 'how2' as StringKey,
              b: 'how2Body' as StringKey
            }, bookingLive ? {
              k: 'how3' as StringKey,
              b: 'how3Body' as StringKey
            } : {
              k: 'how3Preview' as StringKey,
              b: 'how3PreviewBody' as StringKey
            }].map((s, i) => <li key={s.k} className="border-t-2 border-ink pt-4" data-id="element-513" data-mp="393hzqa58cuxs" data-mpi={i}>
                  <span className="font-display text-sm font-black text-magenta" data-id="element-514" data-mp="348cezenkdmba">{i + 1}</span>
                  <h3 className="mt-1 font-display text-lg font-extrabold leading-tight text-ink" data-id="element-515" data-mp="fdb31foiyfp2">{t(s.k)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-ink-600" data-id="element-516" data-mp="kb2xohgk0fwy">{t(s.b)}</p>
                </li>)}
            </ol>
            <BrandPhoto role="loading" alt="Our crew loading a truck" className="mt-8 aspect-[21/9] w-full rounded-2xl" icon={<UserRoundIcon className="h-10 w-10" strokeWidth={1.5} data-id="element-518" data-mp="3stikksm1i0un" />} data-id="element-517" data-mp="te41ucluzwxz" />
          </div>
          <div className="space-y-10" data-id="element-519" data-mp="oey3rhrliso">
            <div className="rounded-2xl bg-ink p-6 text-white" data-id="element-520" data-mp="pg9jx2p07ax">
              <h2 className="font-display text-xl font-extrabold" data-id="element-521" data-mp="5nrkyc8mypv9">{t('ratesTitle')}</h2>
              <dl className="mt-4 divide-y divide-ink-700" data-id="element-522" data-mp="aegf5zbpa0y3">
                {[2, 3].map(n => <div key={n} className="flex items-baseline justify-between py-3" data-id="element-523" data-mp="3959s0hgl3sum">
                    <dt className="text-ink-200" data-id="element-524" data-mp="3e3teciz3b6ni">{t(n === 2 ? 'rate2' : 'rate3')}</dt>
                    <dd data-id="element-525" data-mp="3e1gpdluwzw0y">
                      <span className="font-display text-3xl font-black text-lime" data-id="element-526" data-mp="36ktwwiv6vp6s">{usd(config.rates[n], lang)}</span>
                      <span className="text-sm text-ink-300" data-id="element-527" data-mp="31m8vwh8451ha">{t('perHour')}</span>
                    </dd>
                  </div>)}
              </dl>
              <p className="mt-3 text-sm leading-relaxed text-ink-300" data-id="element-528" data-mp="2skj4hbe8e2ji">{t('ratesNote')}</p>
            </div>
            <HelpAnswers screen="home" data-id="element-529" data-mp="2nl69229ann5b" />
          </div>
        </div>
      </section>
      <TrustSection lang={lang} data-id="element-530" data-mp="2iupu2dptrtgu" />
      <PhotoGallery lang={lang} data-id="element-531" data-mp="2iug0kg5kn46e" />
    </>;
}